import React, { createContext, useContext, useEffect, useState } from 'react';
import {
  ProductionReportMeasurementEntryEnum,
  type MaterialUnitQuantities,
  type ProductionReportMeasurementInput,
} from '../utils/types';
import type {
  Material,
  MaterialComponent,
  MaterialRouting,
} from '@/features/match-maker/utils/match-maker.types';
import { getItemIdentifier } from '@/features/match-maker/utils/match-maker.helpers';

type CalculationContextType = {
  measurement: ProductionReportMeasurementInput;
  setMeasurement: React.Dispatch<
    React.SetStateAction<ProductionReportMeasurementInput>
  >;
  material: Material | MaterialComponent | MaterialRouting | null;
  materialUnits: Record<string, string>;
  materialUnitQuantities: MaterialUnitQuantities;
};

const CalculationContext = createContext<CalculationContextType | undefined>(
  undefined
);

export const initialMeasurement: ProductionReportMeasurementInput = {
  quantity: 0,
  unit: '',
  entry_type: ProductionReportMeasurementEntryEnum.MANUAL,
  tag_id: null,
  material_id: null,
  routing_id: null,
  component_id: null,
  timestamp: null,
};

export const CalculationProvider: React.FC<{
  children: React.ReactNode;
  material: Material | MaterialComponent | MaterialRouting | null;
  preMeasurement?: ProductionReportMeasurementInput;
  materialUnits: Record<string, string>;
  materialUnitQuantities: MaterialUnitQuantities;
}> = ({
  children,
  material,
  preMeasurement,
  materialUnits,
  materialUnitQuantities,
}) => {
  const [measurement, setMeasurement] =
    useState<ProductionReportMeasurementInput>(
      preMeasurement || initialMeasurement
    );

  useEffect(() => {
    if (material) {
      setMeasurement(prev => ({
        ...prev,
        ...(preMeasurement
          ? preMeasurement
          : {
              quantity: 0,
              unit: '',
              entry_type: ProductionReportMeasurementEntryEnum.MANUAL,
              tag_id: null,
              timestamp: null,
            }),
        material_id: getItemIdentifier('material', material),
        routing_id: getItemIdentifier('routing', material),
        component_id: getItemIdentifier('component', material),
      }));
    }
  }, [material, preMeasurement, materialUnits]);

  return (
    <CalculationContext.Provider
      value={{
        measurement,
        setMeasurement,
        material,
        materialUnits,
        materialUnitQuantities,
      }}
    >
      {children}
    </CalculationContext.Provider>
  );
};

export const useCalculationContext = (): CalculationContextType => {
  const context = useContext(CalculationContext);
  if (!context) {
    throw new Error(
      'useCalculationContext must be used within a CalculationProvider'
    );
  }
  return context;
};
